import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getCourseDetailById, getCourseLessons, getCourseResources, getCourseReviews, createCourseCertificate, markLessonCompleted } from '../../api/courses';
import { SEO } from '../../components/SEO';
import { CustomVideoPlayer } from './CustomVideoPlayer';
import './CoursePlayer.scss';

export const CourseDetail = () => {
  const location = useLocation();
  const id = location.state?.id;
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((s) => s.auth || {});
  const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');

  const [courseData, setCourseData] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [resources, setResources] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const queryParams = new URLSearchParams(location.search);
  const lessonIdParam = queryParams.get('lessonId');

  const [selectedLesson, setSelectedLesson] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [openModules, setOpenModules] = useState(['mod-1']);

  const [allCompleted, setAllCompleted] = useState(false);
  const [isDownloadingCert, setIsDownloadingCert] = useState(false);
  const [markingDone, setMarkingDone] = useState(false);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [certificateUrl, setCertificateUrl] = useState(null);
  const [certificateData, setCertificateData] = useState(null);

  const handleMarkComplete = async () => {
    if (!selectedLesson) return;
    try {
      setMarkingDone(true);
      const lessonId = selectedLesson._id || selectedLesson.id;
      const payload = {
        course_id: id,
        lesson_id: lessonId,
        courseId: id,
        lessonId: lessonId,
        userId: currentUser._id || currentUser.id
      };
      await markLessonCompleted(payload);
      setCompletedLessons((prev) => [...prev, lessonId]);
      alert('Lesson marked as completed successfully!');
    } catch (err) {
      console.error(err);
      alert('Failed to mark lesson as completed.');
    } finally {
      setMarkingDone(false);
    }
  };

  const handleDownloadCertificate = async () => {
    // Open a new blank tab synchronously to prevent popup blockers
    const certWindow = window.open('about:blank', '_blank');
    if (certWindow) {
      certWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Loading Certificate...</title>
            <style>
              body {
                background-color: #0f172a;
                color: #ffffff;
                font-family: system-ui, -apple-system, sans-serif;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100vh;
                margin: 0;
              }
              .spinner {
                width: 40px;
                height: 40px;
                border: 4px solid rgba(255,255,255,0.1);
                border-left-color: #eab308;
                border-radius: 50%;
                animation: spin 1s linear infinite;
                margin-bottom: 20px;
              }
              @keyframes spin { 100% { transform: rotate(360deg); } }
            </style>
          </head>
          <body>
            <div class="spinner"></div>
            <h3>Generating your course certificate...</h3>
            <p style="color: #94a3b8; font-size: 14px;">Please wait a moment.</p>
          </body>
        </html>
      `);
    }

    try {
      setIsDownloadingCert(true);
      const res = await createCourseCertificate({
        courseId: id,
        userId: currentUser._id || currentUser.id
      });
      let data = res;
      let certUrl = null;

      // Handle Blob response (due to responseType: 'blob')
      if (res instanceof Blob) {
        try {
          const text = await res.text();
          data = JSON.parse(text);
        } catch (e) {
          certUrl = window.URL.createObjectURL(res);
        }
      }

      if (!certUrl && data) {
        setCertificateData(data.data || data);
        certUrl =
          data.data?.s3Url ||
          data.s3Url ||
          data.data?.pdfUrl ||
          data.pdfUrl ||
          data.data?.url ||
          data.url ||
          (typeof data.data === 'string' && data.data.startsWith('http') ? data.data : null);
      }

      if (certUrl) {
        setCertificateUrl(certUrl);

        if (certWindow) {
          certWindow.location.href = certUrl;
        } else {
          window.open(certUrl, '_blank');
        }

        // Also trigger automatic download of the file
        try {
          const response = await fetch(certUrl);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          const filename = data?.data?.certificateId ? `${data.data.certificateId}.png` : `Certificate-${id}.png`;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (downloadErr) {
          console.warn('Direct blob download warning:', downloadErr);
        }
      } else {
        if (certWindow) certWindow.close();
        alert('Certificate generated successfully, please check your profile!');
      }
    } catch (err) {
      console.error('Certificate error:', err);
      if (certWindow) certWindow.close();
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to generate or download certificate.';
      alert(errMsg);
    } finally {
      setIsDownloadingCert(false);
    }
  };

  const handleResourceDownload = (url, filename) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const formatDuration = (val) => {
      if (!val && val !== 0) return '00:00';
      if (typeof val === 'string' && val.includes(':')) return val;
      const num = parseInt(val, 10);
      if (isNaN(num)) return val;
      const m = Math.floor(num / 60);
      const s = num % 60;
      return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch real course data
        const res = await getCourseDetailById(id);
        let cData = res && res.data ? res.data : null;
        if (!cData && res && !res.status) cData = res; // fallback if it's the root object and not just a status wrapper

        if (cData) {
          setCourseData(cData);
        } else {
          setCourseData({ title: 'Course Details', category_name: 'Education' });
        }

        // Fetch lessons, resources, and reviews concurrently
        const [lessonsRes, resourcesRes, reviewsRes] = await Promise.allSettled([
          getCourseLessons(id),
          getCourseResources(id),
          getCourseReviews(id)
        ]);

        if (resourcesRes.status === 'fulfilled') {
          const resData = resourcesRes.value?.data || resourcesRes.value;
          if (Array.isArray(resData)) {
            setResources(resData);
          } else if (resData && Array.isArray(resData.data)) {
            setResources(resData.data);
          } else if (resData) {
            setResources([resData]);
          } else {
            setResources([]);
          }
        }
        if (reviewsRes.status === 'fulfilled' && reviewsRes.value?.data) {
          setReviews(reviewsRes.value.data);
        }

        let fetchedLessons = [];
        if (lessonsRes.status === 'fulfilled') {
          const lesData = lessonsRes.value?.data || lessonsRes.value;
          if (Array.isArray(lesData)) {
            fetchedLessons = lesData;
          } else if (lesData && Array.isArray(lesData.data)) {
            fetchedLessons = lesData.data;
          }
          if (lessonsRes.value?.all_completed === true) {
            setAllCompleted(true);
          }
        }

        // Group lessons into modules dynamically
        const dynamicModules = [];
        if (fetchedLessons.length > 0) {
          fetchedLessons.forEach((lesson, index) => {
            const modName = lesson.module_name || lesson.module || `Module ${Math.floor(index / 5) + 1}`;
            let mod = dynamicModules.find((m) => m.title === modName);
            if (!mod) {
              mod = {
                id: `mod-${dynamicModules.length + 1}`,
                title: modName,
                completedCount: 0,
                totalCount: 0,
                lessons: [],
              };
              dynamicModules.push(mod);
            }
            const isCompleted = lesson.isVideoCompleted || lesson.is_completed || lesson.completed;
            if (isCompleted) mod.completedCount++;
            mod.totalCount++;

            const isLocked = lesson.is_enable_next === false;

            let status = 'locked';
            if (isCompleted) status = 'completed';
            else if (!isLocked) status = 'circle';

            let rawDuration = lesson.duration || lesson.videoDuration || '05:00';
            const formattedDuration = formatDuration(rawDuration);

            mod.lessons.push({
              ...lesson,
              id: lesson._id || lesson.id || `l-${index}`,
              num: `${mod.lessons.length + 1}.`,
              title: lesson.title,
              duration: formattedDuration,
              status: status,
              originalStatus: status
            });
          });
        }

        if (dynamicModules.length > 0) {
          // Set first unlocked as active, or just first lesson
          const firstUnlocked = dynamicModules[0].lessons[0];
          if (firstUnlocked) {
            firstUnlocked.status = 'active';
            setSelectedLesson(firstUnlocked);
          }
          setLessons(dynamicModules);
          setOpenModules([dynamicModules[0].id]);
        } else {
          setLessons([]);
        }

      } catch (err) {
        console.error("Failed to load course player data:", err);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchData();
    }
  }, [id, lessonIdParam]);

  if (loading || !courseData) {
    return <div className="course-player-page justify-content-center align-items-center bg-dark text-white d-flex"><div className="spinner-border text-yellow"></div></div>;
  }

  const categoryName = courseData.category_name || 'Education';
  const lessonTitle = selectedLesson ? `${selectedLesson.num} ${selectedLesson.title}` : '1. Self-Introduction';

  const toggleModule = (modId) => {
    setOpenModules(prev =>
      prev.includes(modId) ? prev.filter(id => id !== modId) : [...prev, modId]
    );
  };

  return (
    <div className="course-player-page">
      <SEO title={lessonTitle} />

      {/* Top Navigation */}
      <div className="player-top-nav">
        <div className="breadcrumbs">
          <Link to="/courses">Courses</Link>
          <i className="bi bi-chevron-right"></i>
          <Link to="/courses">{categoryName}</Link>
          <i className="bi bi-chevron-right"></i>
          <Link to="/course-detail" state={{ id }}>{courseData.title}</Link>
          <i className="bi bi-chevron-right"></i>
          <span className="active">{lessonTitle}</span>
        </div>
        <div className="top-actions">
          {/* <button className="btn-nav-action"><i className="bi bi-file-earmark-text"></i> Notes</button>
          <button className="btn-nav-action"><i className="bi bi-arrows-angle-expand"></i> Fullscreen</button> */}
          <button className="btn-nav-action" onClick={() => navigate(`/courses`)}><i className="bi bi-box-arrow-right"></i> Exit Course</button>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="player-main-content">
        <Row className="g-4 m-0">

          {/* Left Column - Player & Details */}
          <Col xl={8} lg={8} md={8} className="p-0 pe-md-3">

            {/* Video Container */}
            <div className="video-container">
              {(() => {
                const activeVideoUrl = selectedLesson ? (selectedLesson.video_url || selectedLesson.media || selectedLesson.videoUrl) : (courseData.coverVideo || courseData.cover_video);

                if (activeVideoUrl) {
                  const fallbackImg = courseData?.cover_image || courseData?.coverImage || courseData?.image || "https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=800&h=450&fit=crop";
                  const isYouTube = activeVideoUrl.includes('youtube.com') || activeVideoUrl.includes('youtu.be');
                  if (isYouTube) {
                    const match = activeVideoUrl.match(/(?:youtu\.be\/|v=)([^?&]+)/);
                    const embedUrl = match ? `https://www.youtube.com/embed/${match[1]}` : activeVideoUrl;
                    return <iframe src={embedUrl} title="Video" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" style={{ width: '100%', height: '100%', border: 'none' }} />;
                  }
                  return <CustomVideoPlayer url={activeVideoUrl} title={lessonTitle} poster={fallbackImg} />;
                }

                // Fallback image if no video
                const fallbackImg = courseData?.cover_image || courseData?.coverImage || courseData?.image || "https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=800&h=450&fit=crop";
                return (
                  <div style={{ position: 'relative', width: '100%', height: '100%', backgroundImage: `url("${fallbackImg}")`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)' }}></div>
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', textAlign: 'center' }}>
                      <i className="bi bi-camera-video-off" style={{ fontSize: '3rem', opacity: 0.5 }}></i>
                      <p className="mt-2 text-white-50">No video available</p>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="lesson-header">
              <div className="title-area">
                <h6 style={{ color: '#818cf8', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.85rem' }}>
                  {courseData?.title || courseData?.course_name || 'Course Details'}
                </h6>
                <h2>{lessonTitle}</h2>
                <div className="lesson-meta">
                  <div className="meta-item"><i className="bi bi-clock"></i> {selectedLesson ? selectedLesson.duration : '00:00'}</div>
                  <div className="meta-item"><i className="bi bi-camera-video"></i> Video Lesson</div>
                  <div className="meta-item"><i className="bi bi-reception-4"></i> {courseData?.level || 'Beginner'}</div>
                </div>
              </div>
              <div className="lesson-actions">
                <button className="btn-action btn-mark-done" onClick={handleMarkComplete} disabled={markingDone || (selectedLesson && completedLessons.includes(selectedLesson._id || selectedLesson.id))}>
                  {markingDone ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-check-lg"></i>}
                  {(selectedLesson && completedLessons.includes(selectedLesson._id || selectedLesson.id)) ? ' Completed' : ' Mark as Done'}
                </button>
                <button className="btn-action btn-outline text-warning border-warning" onClick={handleDownloadCertificate} disabled={isDownloadingCert} title="Download Certificate in next tab">
                  {isDownloadingCert ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-trophy-fill me-1"></i>} Certificate
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="custom-tabs-container">
              <div className="custom-tabs">
                <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                <button className={`tab-btn ${activeTab === 'resources' ? 'active' : ''}`} onClick={() => setActiveTab('resources')}>Resources</button>
                <button className={`tab-btn ${activeTab === 'certificate' ? 'active' : ''}`} onClick={() => setActiveTab('certificate')}>Certificate</button>
                <button className={`tab-btn ${activeTab === 'qa' ? 'active' : ''}`} onClick={() => setActiveTab('qa')}>Reviews ({reviews?.length || 0})</button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <Row className="g-4 m-0">
                <Col md={12} className="p-0">
                  <div className="info-card">
                    <h5>About This {selectedLesson ? 'Lesson' : 'Course'}</h5>
                    <div className="text-white-50" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedLesson?.description || courseData?.description || 'No description available for this lesson.'}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {activeTab === 'certificate' && (
              <div className="info-card">
                <h5 className="mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-patch-check-fill text-warning"></i>
                  Course Certificate
                </h5>
                <p className="text-white-50 mb-4" style={{ fontSize: '0.9rem' }}>
                  Official certificate of completion for {courseData?.title || 'this course'}.
                </p>

                <div className="border rounded p-4 text-center" style={{ borderColor: '#1f2937', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  {certificateUrl ? (
                    <div className="mb-4">
                      <div className="mb-3" style={{ maxHeight: '350px', overflow: 'hidden', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <img src={certificateUrl} alt="Certificate Preview" style={{ maxWidth: '100%', maxHeight: '350px', objectFit: 'contain' }} />
                      </div>
                      {certificateData?.certificateId && (
                        <div className="text-white-50 mb-2 fs-7">
                          Certificate ID: <strong className="text-white">{certificateData.certificateId}</strong>
                        </div>
                      )}
                      <div className="mb-3 text-break fs-7 text-white-50">
                        S3 URL:{' '}
                        <a href={certificateUrl} target="_blank" rel="noopener noreferrer" className="text-warning text-decoration-underline ms-1">
                          {certificateUrl}
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <i className="bi bi-award-fill text-warning display-3 mb-3 d-block"></i>
                      <h6 className="text-white mb-2" style={{ fontWeight: 600 }}>Certificate of Completion</h6>
                      <p className="text-white-50 fs-7 mb-4">
                        Earned upon completing all lessons for <strong>{courseData?.title || 'this course'}</strong>
                      </p>
                    </div>
                  )}

                  <div className="d-flex flex-wrap gap-2 justify-content-center">
                    <button
                      onClick={handleDownloadCertificate}
                      disabled={isDownloadingCert}
                      className="btn btn-warning fw-semibold px-4 py-2 d-flex align-items-center gap-2"
                      style={{ borderRadius: '8px', fontSize: '0.9rem' }}
                    >
                      {isDownloadingCert ? (
                        <>
                          <span className="spinner-border spinner-border-sm"></span>
                          Generating Certificate...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-box-arrow-up-right fs-6"></i>
                          Open in Next Tab & Download
                        </>
                      )}
                    </button>

                    {certificateUrl && (
                      <a
                        href={certificateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-light fw-semibold px-4 py-2 d-flex align-items-center gap-2"
                        style={{ borderRadius: '8px', fontSize: '0.9rem' }}
                      >
                        <i className="bi bi-arrow-up-right-square"></i>
                        View S3 URL in New Tab
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'resources' && (
              <div className="info-card">
                <h5 className="mb-4">All Course Resources</h5>
                {resources && resources.flatMap(res => res.pdf_files || []).length > 0 ? (
                  <Row className="g-3">
                    {resources.flatMap(res => res.pdf_files || []).map((file, idx) => (
                      <Col md={6} key={idx}>
                        <div className="resource-item border rounded p-3" style={{ borderColor: '#1f2937' }}>
                          <div className="r-info">
                            <i className="bi bi-file-earmark-pdf-fill" style={{ color: '#ef4444' }}></i>
                            <div>
                              <div className="r-name">{file.title || file.filename || `Resource ${idx + 1}`}</div>
                              <div className="r-type">PDF Document{file.fileSize ? ` • ${(file.fileSize / 1024).toFixed(1)} KB` : ''}</div>
                            </div>
                          </div>
                          <button onClick={() => handleResourceDownload(file.fileUrl || file.url || file.file_url, file.filename || 'resource.pdf')} className="btn-download" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid #1f2937', color: 'white', borderRadius: '4px', padding: '0.4rem 0.8rem' }}><i className="bi bi-download"></i></button>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="text-center py-4 text-white-50">
                    <i className="bi bi-folder2-open fs-1 mb-2 d-block opacity-50"></i>
                    <p>No resources available for this course yet.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'qa' && (
              <div className="info-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Questions & Answers ({reviews?.length || 0})</h5>
                  <button className="btn btn-primary" style={{ backgroundColor: '#4f46e5', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 600 }}>
                    Ask a new question
                  </button>
                </div>

                <div className="qa-list d-flex flex-column gap-3">
                  {reviews && reviews.length > 0 ? (
                    reviews.map((rev, idx) => (
                      <div key={idx} className="qa-item p-3 border rounded" style={{ borderColor: '#1f2937', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                        <div className="d-flex gap-3 mb-2">
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                            {(rev.user?.full_name || 'U')[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-bold" style={{ fontSize: '0.9rem' }}>{rev.user?.full_name || 'User'}</div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{new Date(rev.createdAt || Date.now()).toLocaleDateString()}</div>
                          </div>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#d1d5db', marginBottom: '0.75rem' }}>{rev.comment || rev.review}</p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-white-50">
                      <i className="bi bi-chat-dots fs-1 mb-2 d-block opacity-50"></i>
                      <p>No questions yet. Be the first to ask!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

          </Col>

          {/* Right Column - Curriculum */}
          <Col xl={4} lg={4} md={4} className="p-0 ps-md-3">
            <div className="sidebar-container">

              {/* Progress Card */}
              <div className="progress-card">
                {(() => {
                  const totalLessons = lessons.reduce((acc, mod) => acc + mod.totalCount, 0);
                  const completedLessons = lessons.reduce((acc, mod) => acc + mod.completedCount, 0);
                  const progressPct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

                  let totalSeconds = 0;
                  lessons.forEach(mod => {
                    if (mod.lessons) {
                      mod.lessons.forEach(l => {
                        const sec = parseInt(l.videoDuration || 0, 10);
                        if (!isNaN(sec)) totalSeconds += sec;
                      });
                    }
                  });
                  let calcDuration = courseData.duration || courseData.hours;
                  if (!calcDuration && totalSeconds > 0) {
                    const h = Math.floor(totalSeconds / 3600);
                    const m = Math.floor((totalSeconds % 3600) / 60);
                    calcDuration = h > 0 ? `${h}h ${m}m` : `${m}m`;
                  } else if (!calcDuration) {
                    calcDuration = '0m';
                  }

                  return (
                    <>
                      <div className="progress-header">
                        <span>Course Progress</span>
                        <span>{progressPct}% Complete</span>
                      </div>
                      <div className="progress-bar-bg">
                        <div className="progress-bar-fill" style={{ width: `${progressPct}%` }}></div>
                      </div>
                      <div className="progress-meta">
                        {totalLessons} Lesson{totalLessons !== 1 ? 's' : ''} • {calcDuration}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Modules List */}
              <div className="modules-list">
                {lessons.map((mod) => {
                  const isOpen = openModules.includes(mod.id);
                  return (
                    <div className="module-card" key={mod.id}>
                      <div className="module-header" onClick={() => toggleModule(mod.id)}>
                        <div className="mod-title">{mod.title}</div>
                        <div className="mod-meta">
                          <span>{mod.completedCount}/{mod.totalCount}</span>
                          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
                        </div>
                      </div>

                      {isOpen && mod.lessons && (
                        <div className="module-lessons">
                          {mod.lessons.map((lesson) => {
                            const isActive = lesson.status === 'active';
                            return (
                              <div
                                key={lesson.id}
                                className={`lesson-item ${isActive ? 'active' : ''}`}
                                onClick={() => setSelectedLesson(lesson)}
                              >
                                <div className="lesson-title-area">
                                  <span className="lesson-num">{lesson.num}</span>
                                  {isActive && <i className="bi bi-play-circle-fill lesson-play-icon"></i>}
                                  <span className="lesson-title">{lesson.title}</span>
                                </div>
                                <div className="lesson-right">
                                  <span className="lesson-duration">{lesson.duration}</span>
                                  {lesson.status === 'completed' && <i className="bi bi-check-circle lesson-status-icon completed"></i>}
                                  {lesson.status === 'circle' && <i className="bi bi-circle lesson-status-icon"></i>}
                                  {lesson.status === 'locked' && <i className="bi bi-lock lesson-status-icon"></i>}
                                  {lesson.status === 'active' && <i className="bi bi-play-circle-fill lesson-status-icon" style={{ opacity: 0 }}></i> /* hidden placeholder */}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          </Col>
        </Row>
      </div>

      {/* Full Width Bottom Navigation */}
      {(() => {
        const allLessons = lessons.reduce((acc, mod) => [...acc, ...mod.lessons], []);
        const currentLessonIdx = allLessons.findIndex(l => l.id === (selectedLesson && selectedLesson.id));
        const prevLesson = currentLessonIdx > 0 ? allLessons[currentLessonIdx - 1] : null;
        const nextLesson = currentLessonIdx >= 0 && currentLessonIdx < allLessons.length - 1 ? allLessons[currentLessonIdx + 1] : null;

        return (
          <div className="bottom-nav-container">
            <div className="nav-side">
              {prevLesson && (
                <>
                  <Link to="#" className="nav-btn-circle" onClick={(e) => { e.preventDefault(); setSelectedLesson(prevLesson); }}><i className="bi bi-chevron-left"></i></Link>
                  <div>
                    <div className="nav-text">Previous Lesson</div>
                    <div className="nav-title">{prevLesson.title}</div>
                    <div className="nav-text mt-1 d-flex align-items-center gap-1">{prevLesson.duration} {prevLesson.originalStatus === 'completed' && <i className="bi bi-check-circle status-icon"></i>}</div>
                  </div>
                </>
              )}
            </div>

            <div className="nav-center">
              {selectedLesson && selectedLesson.originalStatus === 'completed' ? (
                <div className="progress-msg">Great job! You've completed this lesson.</div>
              ) : (
                <div className="progress-msg">Keep going! You're making progress.</div>
              )}
              {nextLesson && (
                <button className="btn-next-main" onClick={() => setSelectedLesson(nextLesson)}>Next Lesson <i className="bi bi-chevron-right ms-2 fs-5"></i></button>
              )}
            </div>

            <div className="nav-side next">
              {nextLesson && (
                <>
                  <div>
                    <div className="nav-text">Next Lesson</div>
                    <div className="nav-title">{nextLesson.title}</div>
                    <div className="nav-text mt-1">{nextLesson.duration}</div>
                  </div>
                  <Link to="#" className="nav-btn-circle" onClick={(e) => { e.preventDefault(); setSelectedLesson(nextLesson); }}><i className="bi bi-chevron-right"></i></Link>
                </>
              )}
            </div>
          </div>
        );
      })()}

    </div>
  );
};
